import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { BiWallet, BiHistory, BiMoney, BiSearch } from "react-icons/bi";
import PageHeading from "../../PageHeading";
import TableSkeleton from "../../TableSkeleton";
import DynamicSelect from "../../DynamicSelect";
import {
  MdClose
} from "react-icons/md";
import AccountExportOptions from "../../AccountExportOptions";
import { useAuth } from "../../../utils/AuthContext";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import Pagination from "../../Shared/Pagination";

const API_URL = "https://api.muktihospital.com/api/pathology-accounts/refer-ledger";
const WITHDRAW_API_URL =
  "https://api.muktihospital.com/api/pathology-agents/withdraw-commission";
const STATEMENT_API_URL =
  "https://api.muktihospital.com/api/pathology-agents/wallet-statement";
const LIVE_CASH_API = "https://api.muktihospital.com/api/pathology-accounts/live-cash";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const BULK_CACHE_KEY = "pathology_account_summary_bulk_recent";

const referParamsToRange = (params) => {
  if (params.startDate && params.endDate) {
    return {
      start: new Date(new Date(params.startDate).setHours(0, 0, 0, 0)),
      end: new Date(new Date(params.endDate).setHours(23, 59, 59, 999)),
    };
  }
  if (params.year && params.month) {
    const y = parseInt(params.year, 10);
    const m = parseInt(params.month, 10);
    return {
      start: new Date(y, m - 1, 1, 0, 0, 0, 0),
      end: new Date(y, m, 0, 23, 59, 59, 999),
    };
  }
  if (params.year) {
    const y = parseInt(params.year, 10);
    return {
      start: new Date(y, 0, 1, 0, 0, 0, 0),
      end: new Date(y, 11, 31, 23, 59, 59, 999),
    };
  }
  return null;
};

const isReferRangeInsideBulk = (range, bulk) => {
  if (!range || !bulk?.windowStart || !bulk?.windowEnd) return false;
  const ws = new Date(bulk.windowStart).getTime();
  const we = new Date(bulk.windowEnd).getTime();
  return range.start.getTime() <= we && range.end.getTime() >= ws;
};

/**
 * Build a Refer Ledger response (bills + pagination + summary + agent options)
 * entirely from the bulk-recent cache, mimicking the shape /refer-ledger
 * returns. Filters to bills that have a refer/agent, optionally narrows by
 * agentId and search query, then paginates client-side.
 */
const buildReferLedgerFromBulk = (range, bulk, opts) => {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const inRange = (iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return t >= startMs && t <= endMs;
  };

  // Base set: every refer bill in range (used for the agent dropdown so it
  // never shrinks, regardless of the active agent filter).
  const globalReferBills = (bulk?.bills || []).filter(
    (b) =>
      b.status !== "Cancelled" &&
      b.refer !== null &&
      b.refer !== undefined &&
      String(b.refer).trim() !== "" &&
      inRange(b.createdAt)
  );

  // Filtered set: narrowed by agent + search for the visible table.
  let matched = globalReferBills;
  if (opts.agentId) {
    matched = matched.filter((b) => b.agentId === opts.agentId);
  }
  if (opts.searchQuery) {
    const q = String(opts.searchQuery).toLowerCase();
    matched = matched.filter(
      (b) =>
        (b.billNumber && String(b.billNumber).toLowerCase().includes(q)) ||
        (b.patientName && String(b.patientName).toLowerCase().includes(q))
    );
  }

  matched = [...matched].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const enrich = (b) => {
    const referAmount = Number(b.netReferAmount ?? b.referAmount) || 0;
    const commissionPaid = Number(b.commissionPaid) || 0;
    const due = referAmount - commissionPaid;
    return {
      ...b,
      agentPhone: "N/A",
      referAmount,
      commissionPaid,
      commissionDue: due < 0 ? 0 : due,
    };
  };

  const enriched = matched.map(enrich);
  const enrichedGlobal = globalReferBills.map(enrich);

  const totalBusiness = enriched.reduce(
    (s, b) => s + (Number(b.netAmount) || 0), 0
  );
  const totalCommission = enriched.reduce(
    (s, b) => s + (Number(b.referAmount) || 0), 0
  );

  const totalGlobalWithdrawn = (bulk?.withdrawals || [])
    .filter((w) => inRange(w.createdAt))
    .filter((w) => {
      if (!opts.agentId) return true;
      const owningBill = (bulk?.bills || []).find(
        (b) => b.billNumber === w.billNumber
      );
      return owningBill?.agentId === opts.agentId;
    })
    .reduce((s, w) => s + (Number(w.amount) || 0), 0);

  // Build agent dropdown options from the GLOBAL set (never shrinks).
  const agentOptions = [{ value: "", label: "All Agents" }];
  const map = new Map();
  for (const item of enrichedGlobal) {
    if (item.agentId && !map.has(item.agentId)) {
      map.set(item.agentId, true);
      agentOptions.push({
        value: item.agentId,
        label: `${item.refer} (${item.agentPhone || "No Phone"})`,
      });
    }
  }

  // Paginate.
  const totalRecords = enriched.length;
  let pageBills = enriched;
  let totalPages = 1;
  if (opts.limit && opts.limit !== "0") {
    const limit = parseInt(opts.limit, 10) || 10;
    const page = parseInt(opts.page, 10) || 1;
    totalPages = Math.ceil(totalRecords / limit) || 1;
    pageBills = enriched.slice((page - 1) * limit, page * limit);
  }

  return {
    bills: pageBills,
    allBills: enrichedGlobal,
    agentOptions,
    pagination: {
      totalRecords,
      totalPages,
      currentPage: parseInt(opts.page, 10) || 1,
      limit: opts.limit === "0" ? 0 : parseInt(opts.limit, 10) || 10,
    },
    summary: {
      totalBills: totalRecords,
      totalBusiness,
      totalCommission,
      totalGlobalWithdrawn,
    },
  };
};

const ReferLedger = () => {
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const { user } = useAuth();
  const canPerformPayout = useMemo(() => {
    const allowedRoles = ["admin", "superadmin", "pathologist"];
    return allowedRoles.includes(user?.role);
  }, [user]);
  const [summary, setSummary] = useState({
    totalBills: 0,
    totalBusiness: 0,
    totalCommission: 0,
    totalGlobalWithdrawn: 0,
  });

  // --- PAGINATION STATES ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  // --- FILTER STATES ---
  const [filterType, setFilterType] = useState("daily");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [allBillsForExport, setAllBillsForExport] = useState([]);
  const [bulkOfflineActive, setBulkOfflineActive] = useState(false);

  // --- MODAL STATES ---
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [permanentAgentOptions, setPermanentAgentOptions] = useState([{ value: "", label: "All Agents" }]);

  const [activeAgent, setActiveAgent] = useState({
    id: "",
    name: "",
    availableBalance: 0,
    billNumber: "",
  });
  const [statementData, setStatementData] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawDescription, setWithdrawDescription] = useState("");
  const [selectedAgentWallet, setSelectedAgentWallet] = useState(null);
  const [cashInHand, setCashInHand] = useState(null);
  const [loadingCash, setLoadingCash] = useState(false);

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "Accounts" },
      { label: "Refer Ledger" },
    ],
    [],
  );

  const agentOptions = useMemo(() => {
    const unique = [{ value: "", label: "All Agents" }];
    const map = new Map();
    for (const item of allBillsForExport) {
      if (item.agentId && !map.has(item.agentId)) {
        map.set(item.agentId, true);
        unique.push({
          value: item.agentId,
          label: `${item.refer} (${item.agentPhone || "No Phone"})`,
        });
      }
    }
    return unique;
  }, [allBillsForExport]);

  const selectedAgentLabel = useMemo(() => {
    const found = agentOptions.find((o) => o.value === selectedAgentId);
    return found ? found.label.split(" (")[0] : "";
  }, [selectedAgentId, agentOptions]);

  const fetchSelectedAgentWallet = useCallback(async (agentId) => {
    if (!agentId) {
      setSelectedAgentWallet(null);
      return;
    }
    try {
      const response = await axios.get(`${STATEMENT_API_URL}/${agentId}`);
      setSelectedAgentWallet(response.data.data);
    } catch (err) {
      console.error("Error fetching wallet sync cards:", err);
    }
  }, []);

  const fetchLiveCash = useCallback(async () => {
    setLoadingCash(true);
    try {
      const res = await axios.get(LIVE_CASH_API, {
        headers: { "x-api-key": API_KEY },
        params: { userName: user?.name || "" }
      });
      setCashInHand(res.data.availableCash ?? 0);
    } catch (err) {
      console.error("Fetch live cash error:", err);
      setCashInHand(0);
    } finally {
      setLoadingCash(false);
    }
  }, [user?.name]);

  useEffect(() => {
    fetchLiveCash();
  }, [fetchLiveCash]);

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    const params = {
      agentId: selectedAgentId || undefined,
      page: currentPage,
      limit: itemsPerPage,
      agentName: searchQuery ? searchQuery : undefined,
    };

    if (filterType === "daily") {
      params.startDate = startDate;
      params.endDate = endDate;
    } else if (filterType === "monthly") {
      const [y, m] = selectedMonth.split("-");
      params.year = y;
      params.month = m;
    } else if (filterType === "yearly") {
      params.year = selectedYear;
    }

    // One cache slot per filter combination (agent + filterType + page + query).
    const cacheKey = `pathology_refer_ledger_${JSON.stringify(params)}`;

    try {
      const response = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: params,
      });

      setBills(response.data.bills || []);
      if (response.data.summary) {
        setSummary({
          totalBills: response.data.summary.totalBills || 0,
          totalBusiness: response.data.summary.totalBusiness || 0,
          totalCommission: response.data.summary.totalCommission || 0,
          totalGlobalWithdrawn: response.data.summary.totalGlobalWithdrawn || 0,
        });
      }

      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalRecords(response.data.pagination.totalRecords || 0);
      } else {
        setTotalRecords(response.data.bills?.length || 0);
      }

      const exportParams = { ...params, limit: "0" };
      const globalExportParams = { ...exportParams, agentId: undefined };

      const exportResponse = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: globalExportParams,
      });

      const allFetchedBills = exportResponse.data.bills || [];
      setAllBillsForExport(allFetchedBills);

      const uniqueOptions = [{ value: "", label: "All Agents" }];
      const map = new Map();
      for (const item of allFetchedBills) {
        if (item.agentId && !map.has(item.agentId)) {
          map.set(item.agentId, true);
          uniqueOptions.push({
            value: item.agentId,
            label: `${item.refer} (${item.agentPhone || "No Phone"})`,
          });
        }
      }
      setPermanentAgentOptions(uniqueOptions);

      setBulkOfflineActive(false);
      saveToCache(cacheKey, {
        bills: response.data.bills || [],
        summary: response.data.summary || null,
        pagination: response.data.pagination || null,
        allBillsForExport: allFetchedBills,
        permanentAgentOptions: uniqueOptions,
      });
    } catch (err) {
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(cacheKey);
        if (cached && cached.bills) {
          setBills(cached.bills || []);
          if (cached.summary) {
            setSummary({
              totalBills: cached.summary.totalBills || 0,
              totalBusiness: cached.summary.totalBusiness || 0,
              totalCommission: cached.summary.totalCommission || 0,
              totalGlobalWithdrawn: cached.summary.totalGlobalWithdrawn || 0,
            });
          }
          if (cached.pagination) {
            setTotalPages(cached.pagination.totalPages || 1);
            setTotalRecords(cached.pagination.totalRecords || 0);
          }
          setAllBillsForExport(cached.allBillsForExport || []);
          if (cached.permanentAgentOptions) {
            setPermanentAgentOptions(cached.permanentAgentOptions);
          }
          setBulkOfflineActive(false);
          toast.info("Offline: showing cached ledger.");
        } else {
          const bulk = await readFromCache(BULK_CACHE_KEY);
          const range = referParamsToRange(params);
          if (bulk && isReferRangeInsideBulk(range, bulk)) {
            const built = buildReferLedgerFromBulk(range, bulk, {
              agentId: params.agentId,
              searchQuery: params.agentName,
              page: params.page,
              limit: params.limit,
            });
            setBills(built.bills);
            setSummary({
              totalBills: built.summary.totalBills,
              totalBusiness: built.summary.totalBusiness,
              totalCommission: built.summary.totalCommission,
              totalGlobalWithdrawn: built.summary.totalGlobalWithdrawn,
            });
            setTotalPages(built.pagination.totalPages);
            setTotalRecords(built.pagination.totalRecords);
            setAllBillsForExport(built.allBills);
            setPermanentAgentOptions(built.agentOptions);
            setBulkOfflineActive(true);
            toast.info("Offline: showing computed ledger from last 3 months.");
          } else {
            setBills([]);
            setSummary({
              totalBills: 0,
              totalBusiness: 0,
              totalCommission: 0,
              totalGlobalWithdrawn: 0,
            });
            setTotalPages(1);
            setTotalRecords(0);
            setAllBillsForExport([]);
            setBulkOfflineActive(false);
            toast.info(
              "Offline: only last 3 months of data is available. Please connect to internet for older records.",
            );
          }
        }
      } else {
        console.error("Ledger fetch error:", err);
        toast.error("Failed to load ledger data");
      }
    } finally {
      setLoading(false);
    }
  }, [
    filterType,
    startDate,
    endDate,
    selectedMonth,
    selectedYear,
    selectedAgentId,
    searchQuery,
    currentPage,
    itemsPerPage,
  ]);

  const fetchExportData = useCallback(async () => {
    try {
      const params = { agentId: selectedAgentId, limit: "0" };
      if (filterType === "daily") {
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (filterType === "monthly") {
        const [y, m] = selectedMonth.split("-");
        params.year = y;
        params.month = m;
      } else if (filterType === "yearly") {
        params.year = selectedYear;
      }

      const response = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: params,
      });
      setAllBillsForExport(response.data.bills || []);
    } catch (err) {
      console.error("Export fetch error:", err);
    }
  }, [
    filterType,
    startDate,
    endDate,
    selectedMonth,
    selectedYear,
    selectedAgentId,
  ]);

  useEffect(() => {
    fetchLedger();
    fetchExportData();
    if (selectedAgentId) {
      fetchSelectedAgentWallet(selectedAgentId);
    } else {
      setSelectedAgentWallet(null);
    }
  }, [fetchLedger, fetchExportData, selectedAgentId, fetchSelectedAgentWallet]);

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();

    if (!canPerformPayout) {
      return toast.error(
        "Access Denied! You do not have permission to pay commission.",
      );
    }

    const enteredAmount = parseFloat(withdrawAmount || 0);
    if (!withdrawAmount || enteredAmount <= 0) {
      return toast.warn("Please enter a valid amount");
    }

    if (cashInHand !== null && (cashInHand <= 0 || enteredAmount > cashInHand)) {
      return toast.error(`Insufficient Cash in Hand! Available: ৳${cashInHand.toFixed(2)}, Requested: ৳${enteredAmount.toFixed(2)}`);
    }

    setWithdrawLoading(true);
    try {
      await axios.post(WITHDRAW_API_URL, {
        agentId: activeAgent.id,
        amount: enteredAmount,
        billNumber: activeAgent.billNumber || null,
        description: withdrawDescription,
        userName: user?.name || "Unknown Staff",
      });

      toast.success("Cash Out Log Posted Successfully!");
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      setWithdrawDescription("");

      fetchLedger();
      fetchLiveCash();
      if (selectedAgentId) fetchSelectedAgentWallet(selectedAgentId);
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Transaction compilation failed.",
      );
    } finally {
      setWithdrawLoading(false);
    }
  };

  const openStatementModal = async (agentId, agentName) => {
    setStatementLoading(true);
    setStatementData(null);
    setShowStatementModal(true);
    try {
      const response = await axios.get(`${STATEMENT_API_URL}/${agentId}`);
      setStatementData({ ...response.data.data, name: agentName });
    } catch (err) {
      toast.error("Could not fetch wallet history");
    } finally {
      setStatementLoading(false);
    }
  };

  const openWithdrawModal = (bill) => {
    fetchLiveCash();
    if (!canPerformPayout) {
      return toast.error("You do not have permission to disburse cash!");
    }

    if (!bill.agentId) return toast.warn("No agent associated with this bill");

    const activeDue =
      bill.commissionDue !== undefined ? bill.commissionDue : bill.referAmount;

    setActiveAgent({
      id: bill.agentId,
      name: bill.refer,
      billNumber: bill.billNumber,
      availableBalance: activeDue,
      mainCommission: bill.referAmount || 0,
      alreadyPaid: bill.commissionPaid || 0,
    });
    setWithdrawAmount(activeDue);
    setWithdrawDescription(`Commission cash out for Bill: ${bill.billNumber}`);
    setShowWithdrawModal(true);
  };

  const openBulkWithdrawModal = async () => {
    fetchLiveCash();
    if (!selectedAgentId) return toast.warn("Please select an agent first");

    setLoading(true);
    try {
      const response = await axios.get(
        `${STATEMENT_API_URL}/${selectedAgentId}`,
      );
      const wallet = response.data.data;

      setActiveAgent({
        id: selectedAgentId,
        name: selectedAgentLabel,
        billNumber: "",
        availableBalance: wallet.availableBalance || 0,
      });

      setWithdrawAmount(wallet.availableBalance || 0);
      setWithdrawDescription(
        `Bulk account payout processed by Accounts department`,
      );
      setShowWithdrawModal(true);
    } catch (err) {
      toast.error("Failed to sync agent wallet balance");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const skeletonColumns = [
    { width: 40, height: 16 },
    { width: 80, height: 16 },
    { width: 90, height: 16 },
    { width: 140, height: 16 },
    { width: 130, height: 16 },
    { width: 90, height: 16 },
    { width: 90, height: 16 },
    { width: 90, height: 16, className: "text-right" },
    { width: 80, height: 16, className: "text-center" },
  ];

  const headers = [
    "SL",
    "Date",
    "Bill No",
    "Agent Name",
    "Agent Phone",
    "Net Amount",
    "Commission",
  ];

  const dataMapper = (row) => {
    const index = allBillsForExport.findIndex((item) => item.id === row.id);
    const actualReceived =
      row.commissionPaid !== undefined ? row.commissionPaid : 0;

    return [
      index + 1,
      new Date(row.createdAt).toLocaleDateString("en-GB"),
      row.billNumber,
      row.refer,
      row.agentPhone || "N/A",
      row.netAmount.toFixed(2),
      actualReceived.toFixed(2),
    ].join("\t");
  };

const getFilterSubtitle = () => {
  let rangeText = "";
  if (filterType === "daily") {
    const fStart = new Date(startDate).toLocaleDateString("en-GB");
    const fEnd = new Date(endDate).toLocaleDateString("en-GB");
    rangeText = `Date: ${fStart === fEnd ? fStart : `${fStart} - ${fEnd}`}`;
  } else if (filterType === "monthly") {
    const [year, month] = selectedMonth.split("-");
    const dateObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    rangeText = `Month: ${dateObj.toLocaleString("en-US", { month: "long", year: "numeric" })}`;
  } else {
    rangeText = `Year: ${selectedYear}`;
  }

  return `${rangeText}   |   Total Referrals: ${summary.totalBills}`;
};

const summaryFooter = [
  [
    {
      content: `TOTAL (${summary.totalBills} Bills)`,
      colSpan: 5,
      styles: { fontStyle: "bold", halign: "left" },
    },
    {
      content: summary.totalBusiness.toFixed(2),
      styles: { fontStyle: "bold", halign: "left" },
    },
    {
      content: summary.totalCommission.toFixed(2),
      styles: { fontStyle: "bold", halign: "left" },
    },
  ],
];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <PageHeading
        title="Refer Ledger & Agent Wallet"
        breadcrumbs={breadcrumbs}
      />

      {bulkOfflineActive && (
        <div className="print-hidden mb-3 rounded-md border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm text-orange-700 flex items-center gap-2">
          <span className="font-semibold">⚠ Offline mode</span>
          <span>— Showing data from last 3 months only. Connect to internet for older records.</span>
        </div>
      )}

      {/* --- Filter Section --- */}
      <div className="bg-white shadow-sm rounded-lg mb-6">
        <div className="p-4 flex items-center justify-between gap-4 flex-wrap print-hidden">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-[320px]">
              <label className="text-sm font-bold text-gray-600 whitespace-nowrap">
                Select Agent:
              </label>
              <div className="w-full">
                <DynamicSelect
                  options={permanentAgentOptions}
                  value={
                    permanentAgentOptions.find((o) => o.value === selectedAgentId) ||
                    permanentAgentOptions[0]
                  }
                  onChange={(o) => {
                    setSelectedAgentId(o ? o.value : "");
                    setCurrentPage(1);
                  }}
                  isSearchable={true}
                  isClearable={true}
                  placeholder="Type Agent Name or Phone..."
                />
              </div>
            </div>
            <div className="flex items-center gap-2 relative min-w-[240px]">
              <span className="absolute left-3 text-gray-400">
                <BiSearch size={16} />
              </span>
              <input
                type="text"
                placeholder="Search Bill No or Patient Name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 pr-4 py-2 w-full border rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {selectedAgentId && canPerformPayout && (
              <button
                onClick={openBulkWithdrawModal}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-xs font-black rounded-md flex items-center gap-1.5 shadow-md transition-all animate-fadeIn"
              >
                <BiMoney size={16} />
                Withdraw Agent Capital (Bulk)
              </button>
            )}

            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-600">Filter:</label>
              <div className="w-28">
                <DynamicSelect
                  options={filterOptions}
                  value={filterOptions.find((o) => o.value === filterType)}
                  onChange={(o) => setFilterType(o.value)}
                  isSearchable={false}
                />
              </div>
            </div>

            {filterType === "daily" && (
              <div className="flex items-center gap-2 animate-fadeIn">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border rounded-md p-1.5 text-sm outline-none"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border rounded-md p-1.5 text-sm outline-none"
                />
              </div>
            )}

            {filterType === "monthly" && (
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border rounded-md p-1.5 text-sm outline-none animate-fadeIn"
              />
            )}

            {filterType === "yearly" && (
              <input
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="border rounded-md p-1.5 text-sm outline-none w-24 animate-fadeIn"
                min="2000"
                max="2100"
              />
            )}

            <AccountExportOptions
              data={allBillsForExport}
              title="Refer Ledger Report"
              subTitle={getFilterSubtitle()}
              headers={headers}
              dataMapper={dataMapper}
              footer={summaryFooter}
              printedBy={user?.name || "Accounts"}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
            />
          </div>
        </div>
      </div>

      {/* --- Dynamic Summary Cards------*/}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {selectedAgentWallet ? (
          <>
            <div className="bg-white p-5 rounded-xl border-l-4 border-blue-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Business Brought
              </p>
              <p className="text-2xl font-black text-blue-600">
                ৳{" "}
                {Number(
                  selectedAgentWallet.totalBusinessGenerated || 0,
                ).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-gray-700 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Earned Commission
              </p>
              <p className="text-2xl font-black text-gray-800">
                ৳ {Number(selectedAgentWallet.totalBalance || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-red-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Withdrawn Paid
              </p>
              <p className="text-2xl font-black text-red-600">
                ৳ {Number(selectedAgentWallet.amountReceived || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-emerald-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Net Available Balance
              </p>
              <p className="text-2xl font-black text-emerald-600">
                ৳ {Number(selectedAgentWallet.availableBalance || 0).toFixed(2)}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white p-5 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Referrals Count
              </p>
              <p className="text-2xl font-black text-gray-800">
                {summary.totalBills}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-emerald-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Global Business
              </p>
              <p className="text-2xl font-black text-emerald-600">
                ৳ {Number(summary.totalBusiness || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-orange-500 shadow-sm">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Global Commission Generated
              </p>
              <p className="text-2xl font-black text-orange-600">
                ৳ {Number(summary.totalCommission || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl border-l-4 border-red-500 shadow-sm animate-fadeIn">
              <p className="text-gray-400 text-[10px] font-black uppercase tracking-wider">
                Total Global Withdrawn
              </p>
              <p className="text-2xl font-black text-red-600">
                ৳ {Number(summary.totalGlobalWithdrawn || 0).toFixed(2)}
              </p>
            </div>
          </>
        )}
      </div>

      {/* --- Data Table --- */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">
                  SL
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">
                  Date
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">
                  Bill No
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">
                  Patient Name
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">
                  Agent (Refer)
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">
                  Agent Phone
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px]">
                  Net Amount
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-right">
                  Commission
                </th>
                <th className="p-3 font-bold text-gray-600 uppercase text-[11px] text-center print-hidden">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                />
              ) : bills.length === 0 ? (
                <tr>
                  <td colSpan="9" className="p-10 text-center text-gray-400">
                    No records found.
                  </td>
                </tr>
              ) : (
                bills.map((bill, index) => (
                  <tr
                    key={bill.id}
                    className="border-b last:border-none hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-3 text-gray-500">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="p-3 text-gray-600">
                      {new Date(bill.createdAt).toLocaleDateString("en-GB")}
                    </td>
                    <td className="p-3 font-bold text-gray-800">
                      {bill.billNumber}
                    </td>
                    <td className="p-3 text-gray-700 capitalize">
                      {bill.patientName?.toLowerCase()}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() =>
                          openStatementModal(bill.agentId, bill.refer)
                        }
                        className="text-blue-600 font-bold hover:underline bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded text-[12px] capitalize"
                      >
                        {bill.refer?.toLowerCase()}
                      </button>
                    </td>
                    <td className="p-3 text-gray-500 font-mono">
                      {bill.agentPhone || "N/A"}
                    </td>
                    <td className="p-3 font-semibold text-gray-700">
                      ৳ {bill.netAmount.toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-black text-orange-600">
                      <div className="flex flex-col items-end text-xs">
                        <div className="text-sm font-black text-orange-600">
                          Commission: ৳{(bill.netReferAmount ?? bill.referAmount).toFixed(2)}
                        </div>

                        {(bill.referAmount - (bill.netReferAmount ?? bill.referAmount)) > 0 && (
                          <div className="text-gray-400 text-[10px] font-normal mt-0.5">
                            (Gross ৳{bill.referAmount.toFixed(2)} − discount ৳{(bill.referAmount - (bill.netReferAmount ?? bill.referAmount)).toFixed(2)})
                          </div>
                        )}

                        <div
                          className="text-sm font-black text-gray-800 mt-0.5"
                          title={`Paid: ৳${bill.commissionPaid.toFixed(2)}`}
                        >
                          Due: ৳{bill.commissionDue.toFixed(2)}
                        </div>

                        <span
                          className={`text-[9px] px-1 rounded mt-1 font-bold ${
                            bill.commissionStatus === "Paid"
                              ? "bg-green-100 text-green-700"
                              : bill.commissionStatus === "Partial"
                                ? "bg-blue-100 text-blue-700 font-black"
                                : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {bill.commissionStatus === "Partial"
                            ? `Partial`
                            : bill.commissionStatus || "Unpaid"}
                        </span>
                      </div>
                    </td>

                    <td className="p-3 flex items-center justify-center gap-1.5 print-hidden">
                      <button
                        onClick={() => openWithdrawModal(bill)}
                        disabled={
                          bill.commissionStatus === "Paid" ||
                          bill.status === "Pending" ||
                          !canPerformPayout
                        }
                        title={
                          !canPerformPayout
                            ? "Forbidden: You don't have access"
                            : bill.commissionStatus === "Paid"
                              ? "Already Cashed Out"
                              : bill.status === "Pending"
                                ? "Locked: Patient Due"
                                : "Instant Payout"
                        }
                        className={`p-1.5 rounded-lg transition-all duration-150 border shadow-sm ${
                          bill.commissionStatus === "Paid" ||
                          bill.status === "Pending" ||
                          !canPerformPayout
                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                            : "bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white border border-emerald-200"
                        }`}
                      >
                        <BiWallet size={15} />
                      </button>

                      <button
                        onClick={() =>
                          openStatementModal(bill.agentId, bill.refer)
                        }
                        title="View Statement / Ledger Records"
                        className="bg-gray-50 text-gray-600 hover:bg-gray-700 hover:text-white p-1.5 rounded-lg transition-all duration-150 border border-gray-200 shadow-sm"
                      >
                        <BiHistory size={15} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* --- Reusable Pagination --- */}
        <div className="print-hidden">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {showWithdrawModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-fadeIn">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-xl border border-gray-100 overflow-hidden transform transition-all">
            <div className="bg-emerald-600 p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-1.5">
                <BiWallet size={18} />
                <h3 className="font-bold text-sm">
                  {activeAgent.billNumber
                    ? "Bill-Wise Payout"
                    : "Bulk Account Cash Out"}
                </h3>
              </div>
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="bg-white/20 p-1 rounded-full"
              >
                <MdClose size={18} />
              </button>
            </div>

            <form onSubmit={handleWithdrawSubmit} className="p-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg border text-xs space-y-1.5">
                <p className="text-gray-400 font-medium">
                  Agent:{" "}
                  <span className="font-bold text-gray-700 capitalize">
                    {activeAgent.name?.toLowerCase()}
                  </span>
                </p>
                <p className="text-gray-400">
                  Type:{" "}
                  <span className="font-bold text-blue-600">
                    {activeAgent.billNumber
                      ? `Single Bill (${activeAgent.billNumber})`
                      : "Global Wallet Bulk Out"}
                  </span>
                </p>

                {activeAgent.billNumber && (
                  <div className="pt-1.5 mt-1 border-t border-dashed border-gray-200 text-gray-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Main Commission:</span>
                      <span className="font-semibold text-gray-700">
                        ৳ {activeAgent.mainCommission?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Already Paid:</span>
                      <span className="font-semibold text-emerald-600">
                        ৳ {activeAgent.alreadyPaid?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2 mt-1 border-t border-dashed border-gray-300">
                  <span className="font-bold text-gray-600">
                    {activeAgent.billNumber
                      ? "Net Due to Pay:"
                      : "Net Wallet Balance:"}
                  </span>
                  <span className="text-sm font-black text-red-600">
                    ৳ {activeAgent.availableBalance?.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                <span className="font-bold text-blue-900">Your Cash in Hand:</span>
                <span className={`font-black ${cashInHand <= 0 ? "text-red-600" : "text-emerald-700"}`}>
                  {loadingCash ? "Checking..." : `৳ ${(cashInHand || 0).toFixed(2)}`}
                </span>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600">
                  Payout Amount (৳)
                </label>
                <input
                  type="number"
                  required
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className={`w-full border rounded-lg p-2 text-sm outline-none font-bold text-gray-800 ${
                    cashInHand !== null && Number(withdrawAmount) > cashInHand
                      ? "border-red-500 bg-red-50 focus:ring-red-400"
                      : "focus:ring-2 focus:ring-emerald-500"
                  }`}
                />
                {cashInHand !== null && Number(withdrawAmount) > cashInHand && (
                  <span className="text-[10px] text-red-500 font-bold block mt-1">
                    Payout amount exceeds your cash in hand (৳{(cashInHand || 0).toFixed(2)})!
                  </span>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600">
                  Remarks / Description
                </label>
                <textarea
                  placeholder="Cash out details..."
                  value={withdrawDescription}
                  onChange={(e) => setWithdrawDescription(e.target.value)}
                  className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-emerald-500 h-14 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowWithdrawModal(false)}
                  className="px-3 py-1.5 text-xs text-gray-500 bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    withdrawLoading || 
                    loadingCash || 
                    (cashInHand !== null && (cashInHand <= 0 || Number(withdrawAmount) > cashInHand))
                  }
                  className="px-4 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {withdrawLoading ? "Processing..." : "Confirm Payout"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showStatementModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-fadeIn">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl border overflow-hidden">
            <div className="bg-gray-800 p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <BiHistory size={20} />
                <h3 className="font-bold text-sm">
                  Agent Wallet Ledger Book (Statement)
                </h3>
              </div>
              <button
                onClick={() => setShowStatementModal(false)}
                className="bg-white/20 p-1 rounded-full"
              >
                <MdClose size={18} />
              </button>
            </div>

            <div className="p-5 max-h-[80vh] overflow-y-auto space-y-4">
              {statementLoading ? (
                <div className="text-center py-10 text-xs text-gray-400 font-medium">
                  Loading ledger statement transactions...
                </div>
              ) : !statementData ? (
                <div className="text-center py-10 text-xs text-gray-400">
                  No account history found for this agent.
                </div>
              ) : (
                <>
                  <div className="bg-gray-50 rounded-xl p-4 border grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">
                        Total Business
                      </p>
                      <p className="text-base font-black text-blue-600">
                        ৳ {statementData.totalBusinessGenerated?.toFixed(2)}
                      </p>
                    </div>
                    <div className="border-l">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">
                        Earned Commission
                      </p>
                      <p className="text-base font-black text-gray-800">
                        ৳ {statementData.totalBalance?.toFixed(2)}
                      </p>
                    </div>
                    <div className="border-l">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">
                        Total Withdrawn
                      </p>
                      <p className="text-base font-black text-red-600">
                        ৳ {statementData.amountReceived?.toFixed(2)}
                      </p>
                    </div>
                    <div className="border-l">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">
                        Available Balance
                      </p>
                      <p className="text-base font-black text-emerald-600">
                        ৳ {statementData.availableBalance?.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs font-bold text-gray-700">
                    Account Owner:{" "}
                    <span className="capitalize text-blue-600">
                      {statementData.name?.toLowerCase()}
                    </span>{" "}
                    (ID: {statementData.id})
                  </p>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-gray-100 border-b">
                        <tr>
                          <th className="p-2.5 font-bold text-gray-600">
                            Txn Date
                          </th>
                          <th className="p-2.5 font-bold text-gray-600">
                            Reference Info
                          </th>
                          <th className="p-2.5 font-bold text-gray-600 text-center">
                            Type
                          </th>
                          <th className="p-2.5 font-bold text-gray-600 text-right">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {!statementData.transactions ||
                        statementData.transactions.length === 0 ? (
                          <tr>
                            <td
                              colSpan="4"
                              className="text-center p-4 text-gray-400"
                            >
                              No ledger statements posted yet.
                            </td>
                          </tr>
                        ) : (
                          statementData.transactions.map((txn) => (
                            <tr
                              key={txn.id}
                              className="border-b last:border-none hover:bg-gray-50"
                            >
                              <td className="p-2.5 text-gray-500 font-mono">
                                {new Date(txn.createdAt).toLocaleDateString(
                                  "en-GB",
                                )}
                              </td>
                              <td className="p-2.5">
                                <p className="font-bold text-gray-700">
                                  {txn.description}
                                </p>
                                {txn.billNumber && (
                                  <span className="bg-gray-100 px-1 py-0.5 rounded font-mono text-[10px] text-gray-500">
                                    Bill: {txn.billNumber}
                                  </span>
                                )}
                              </td>
                              <td className="p-2.5 text-center">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[9px] font-black ${txn.type === "COMMISSION" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}
                                >
                                  {txn.type}
                                </span>
                              </td>
                              <td
                                className={`p-2.5 text-right font-bold ${txn.type === "COMMISSION" ? "text-emerald-600" : "text-red-600"}`}
                              >
                                {txn.type === "COMMISSION" ? "+" : "-"} ৳{" "}
                                {txn.amount.toFixed(2)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="p-3 bg-gray-50 border-t flex justify-end">
              <button
                onClick={() => setShowStatementModal(false)}
                className="bg-gray-800 text-white font-bold px-4 py-1.5 text-xs rounded-lg shadow-sm hover:bg-gray-900"
              >
                Close Ledger
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferLedger;